import { useCallback, useContext, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { Projects } from '@trinity/chat/api/interfaces';
import * as Tab from '@/components/presentation/Workspace.Tab.Pane';
import { ProjectTypeMap } from '@/enums';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import { useAppSelector } from '@/store';
import { cx } from '@trinity/chat-components';
import type { ProjectDetailsFormValue } from './interfaces';
import { projectDetailsFormValueSchema } from './interfaces';
import { ProjectDetailsEditForm } from './Project.Details.Form';
import { ProjectNotes } from './Project.Notes';
import { EditFormHeader } from './Project.Notes.FormButtons';
import styles from './style/Project.Details.module.css';
import GroupProjectParentTagsContainer from '@/containers/GroupTags/GroupProjectTagsContainer';
import { useGroupEntityTags, useGroupEntityTagsUpdate } from '@trinity/chat-components/contexts/group-tags/hooks/index.js';
import { GroupSidebarTags } from '@trinity/chat-components/components/GroupSidebarTags/GroupSidebarTags.js';

type Props = unknown;

export const ProjectDetails = (props: Props) => {
  const ctx = useContext(ProjectDetailsContext);

  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const groupFeatures = useAppSelector(state => state.features);
  //const saveOverview = useSaveProjectOverview();
  //const { projectParent } = useProjectState();

  const goal = useMemo(() => {
    if (ctx.query.data?.project.goal.value) {
      return ctx.query.data.project.goal.value;
    }
    return null;
  }, [ctx.query.data?.project.goal]);


  const defaultFormValue = useMemo<ProjectDetailsFormValue>((): ProjectDetailsFormValue => {
    return {
      ...ctx.query.data.project,
      goal: goal,
    };
  }, [ctx.query.data.project, goal]);

  const submitMutation = useMutation({
    mutationFn: async (data: ProjectDetailsFormValue) => {
      // @ts-ignore
      await apiClient.projects.updateProjectDetails({
        projectId: ctx.query.data.project.id,
        ...data,
      });

      return data;
    },
    onError: error => {
      toast(`Error updating project details: ${error}`, { type: 'error' });
    },
    onSuccess: async (data) => {
      setEditing(false);
      await ctx.query.refetch();
    },
  });

  const form = useForm<ProjectDetailsFormValue>({
    resolver: zodResolver(projectDetailsFormValueSchema),
    defaultValues: defaultFormValue,
    disabled: submitMutation.isPending,
  });

  const handleSubmit = form.handleSubmit(
    data => submitMutation.mutateAsync(data),
    e => {
      console.error('Form submission error:', e);
    },
  );

  const apiClient = useChatApiClient();

  const notes = useQuery({
    queryKey: [`get:projects/${ctx.query.data.project.id}/notes`],
    queryFn: () => {
      return apiClient.projects.fetchProjectNoteItems({
        projectId: ctx.query.data.project.id,
      });
    },
    enabled: !!ctx.query.data.project.id,
    placeholderData: {
      items: [],
    },
  });

  const notesMutation = useMutation({
    mutationFn: (data: Omit<Projects.UpdateProjectNoteItems.Request, 'projectId'>) => {
      return apiClient.projects.updateProjectNoteItems({
        projectId: ctx.query.data.project.id,
        ...data,
      });
    },
    mutationKey: [`put:projects/${ctx.query.data.project.id}/notes`],
    onSuccess: data => {
      qc.setQueryData([`get:projects/${ctx.query.data.project.id}/notes`], (old: Projects.FetchProjectNoteItems.Response) => ({
        ...old,
        ...data,
      }));
    },
  });

  return (
    <Tab.Panes.Container className={styles.root}>
      <Tab.Pane.Pane>
        <EditFormHeader
          title="Details"
          isEditing={editing}
          onEdit={() => {
            form.reset(defaultFormValue);
            setEditing(true);
          }}
          onSave={handleSubmit}
          onCancel={() => {
            setEditing(false);
            form.reset(defaultFormValue);
          }}
        />
        {!editing && <ProjectDetailsReadOnly />}
        {editing && <ProjectDetailsEditForm form={form} />}
      </Tab.Pane.Pane>

      <ProjectNotes query={notes} mutation={notesMutation} />

      <GroupProjectParentTagsContainer projectParentId={ctx.query.data.project.parentId}>
        <TagPane />
      </GroupProjectParentTagsContainer>

      {/* <$ProjectFilters>
            <Tab.Pane.Pane>
              <ProjectFilters editable={false} item={project} />
            </Tab.Pane.Pane>
          </$ProjectFilters> */}

    </Tab.Panes.Container>
  );
};

ProjectDetails.displayName = 'Project.Details';

function TagPane() {
  const tags = useGroupEntityTags();
  const updateTags = useGroupEntityTagsUpdate();

  return (
    <Tab.Pane.Pane>
      <GroupSidebarTags
        tags={tags}
        onSave={updateTags} />
    </Tab.Pane.Pane>
  );
}


const ProjectDetailsReadOnly = () => {
  const ctx = useContext(ProjectDetailsContext);
  const goal = useMemo(() => {
    if (ctx.query.data?.project.goal.value) {
      return ctx.query.data.project.goal.value;
    }
    return null;
  }, [ctx.query.data?.project.goal.value]);

  return (
    <Tab.Pane.Grid gridTemplateColumns='repeat(2, 0.5fr)'>
      <Tab.Items.Container>
        <Tab.Item.Container>
          <Tab.Item.TextLabel>Respondent Title</Tab.Item.TextLabel>
          <Tab.Item.Value>
            {ctx.query.data.project.name}
          </Tab.Item.Value>
        </Tab.Item.Container>
        <Tab.Item.Container>
          <Tab.Item.TextLabel>Client Title</Tab.Item.TextLabel>
          <Tab.Item.Value>
            {ctx.query.data.project.clientName || <span className={styles.none}>None</span>}
          </Tab.Item.Value>
        </Tab.Item.Container>
        {/* <Tab.Item.Container>
          <Tab.Item.TextLabel>Client Account</Tab.Item.TextLabel>
          <Tab.Item.Value>{ctx.query.data?.project?.account?.name ?? '-'}</Tab.Item.Value>
        </Tab.Item.Container> */}

        <Tab.Item.Container>
          <Tab.Item.TextLabel>Description</Tab.Item.TextLabel>
          <Tab.Item.Value>{ctx.query.data.project.description}</Tab.Item.Value>
        </Tab.Item.Container>

        <Tab.Item.Container>
          <Tab.Item.TextLabel>Goal</Tab.Item.TextLabel>
          <Tab.Item.Value>
            {goal
              ? goal
              : <div className={styles.noneText}>None</div>}
          </Tab.Item.Value>
        </Tab.Item.Container>
      </Tab.Items.Container>

      <Tab.Items.Container>
        <Tab.Item.Container>
          <Tab.Item.TextLabel>Methodology</Tab.Item.TextLabel>
          <Tab.Item.Value>
            {ProjectTypeMap.getName(ctx.query.data.project.projectTypeId)}
          </Tab.Item.Value>
        </Tab.Item.Container>
        <Tab.Item.Container>
          <Tab.Item.TextLabel>Start Date</Tab.Item.TextLabel>
          <Tab.Item.Value>
            {ctx.query.data.project.startDate && <div className={styles.field}>{format(new Date(ctx.query.data.project.startDate), 'MM/dd/yy')}</div>}
            {!ctx.query.data.project.startDate && <div className={cx(styles.field, styles.na)}>N/A</div>}
          </Tab.Item.Value>
        </Tab.Item.Container>
        <Tab.Item.Container>
          <Tab.Item.TextLabel>Target Complete Date</Tab.Item.TextLabel>
          <Tab.Item.Value>
            {ctx.query.data.project.targetCompleteDate && <div className={styles.field}>{format(new Date(ctx.query.data.project.targetCompleteDate), 'MM/dd/yy')}</div>}
            {!ctx.query.data.project.targetCompleteDate && <div className={cx(styles.field, styles.na)}>N/A</div>}
          </Tab.Item.Value>
        </Tab.Item.Container>
        {/* <$TargetCompany>
                  <Tab.Item.Container>
                    <TargetCompany editable={canEdit} item={project} />
                  </Tab.Item.Container>
                </$TargetCompany> */}

      </Tab.Items.Container>
    </Tab.Pane.Grid>
  );
};


